import React from 'react';

/**
 * The shared "no data" empty state — a chart glyph in a soft circular badge over a bold
 * title and a muted one-line hint. Used everywhere a Search Console panel, table or drill
 * has no rows, so every empty surface reads identically instead of each rolling its own
 * bare line. Centred (vertically and horizontally) with its own min-height so it sits in
 * the middle of whatever card holds it. The glyph is decorative (`aria-hidden`); the title
 * and message carry the meaning.
 *
 * The defaults match the app-wide empty state ("No data for this period"). Callers may
 * override either line, but the whole point is uniformity, so most pass nothing.
 *
 * @param {string} [title]     the bold heading
 * @param {string} [message]   the muted hint below it (pass '' to omit)
 * @param {string} [className] extra classes on the wrapper (e.g. `flex-1` to fill a card)
 */
const NoRecords = ({
  title = 'No data for this period',
  message = 'Nothing to show for the selected dates — try a wider range.',
  className = '',
}) => (
  <div className={`flex w-full flex-col items-center justify-center text-center min-h-[220px] py-10 px-4 ${className}`}>
    <span
      className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
      aria-hidden="true"
    >
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 4v15a1 1 0 0 0 1 1h15" />
        <path d="M8 15l3.5-3.5 2.5 2.5L20 8" />
      </svg>
    </span>
    <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">{title}</p>
    {message && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{message}</p>}
  </div>
);

export default NoRecords;
